/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Giao dịch ngân hàng" (`/sales?tab=bank-transactions`, detail `/bank-transactions/:id`): every
 * transaction the payment methods (SePay, Casso, TinoPay: Cài đặt › Module › Thanh toán) report on
 * the receiving accounts and what became of it — matched to a payment request, waiting for staff
 * (not matched, two connections report the account, or matched with another amount, after the
 * lapse time, to a closed or paid request), or set aside. Staff assign a transaction to a request,
 * credit it to a wallet or set it aside, with a reason (platform-wide `billing.adjust`); an
 * unmatched one can be marked as a gateway's settlement (ADR-028 decision 11: recorded, never
 * credited); subsystems/commerce/facade/adapters/admin-api/src/admin/bank_payments.rs decides.
 */

import { Ban, Landmark, Link2, Store, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import type { BankTransaction, BankTxResolved, BankTxStatus, PaymentModuleInstance, PaymentRequest, QrAccount } from '../../api/areas/commerce';
import type { ToneMap } from '../accounts/shared';
import { Callout, DataTable, FilterBar, Field, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatCount, formatDateTime, formatRelative } from '../../lib/format';
import { mayHandlePayments, transferCode } from '../../lib/qrPayment';
import { useApi } from '../../lib/useApi';
import { modulesUrl } from '../modules/ui';
import { ActionDialog, FormFooter, ReasonField, type DialogBodyProps } from '../operator/shared';
import {
  Amount,
  clearParams,
  CustomerRef,
  FilterSelect,
  FilterText,
  Label,
  mutedSmall,
  Pager,
  paymentPath,
  RefreshButton,
  ResetFilters,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

export const BANK_TX_STATUS: ToneMap = { UNMATCHED: 'warn', REVIEW: 'serious', SETTLED: 'ok', IGNORED: 'neutral' };

const FILTERS = ['OPEN', 'UNMATCHED', 'REVIEW', 'SETTLED', 'IGNORED'] as const;
const SOURCES = ['sepay', 'casso', 'tinopay', 'vnpay', 'staff'] as const;
const FILTER_KEYS = ['status', 'source', 'q'] as const;
// ponytail: the gateways this build has (modules with a payment page); read them from the module
// catalog when a second gateway lands. The server refuses any other (module/NOT_A_GATEWAY).
const GATEWAYS = ['vnpay'] as const;

export const bankTxPath = (id: string) => `/bank-transactions/${encodeURIComponent(id)}`;

/** A request, shown by its transfer code, linking to its page. */
function RequestRef({ id, code }: { id: string | null; code: string | null }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={paymentPath(id)} className="font-mono text-sm font-medium text-primary hover:underline">
      {code ?? id.slice(0, 8)}
    </Link>
  );
}

/** Status and the reason it stands so (both translated). */
function TxState({ t: x }: { t: Pick<BankTransaction, 'status' | 'reason'> }) {
  return (
    <span className="inline-flex flex-col gap-0.5">
      <Status group="bankTxStatus" tones={BANK_TX_STATUS} value={x.status} />
      <span className={mutedSmall}>
        <Label group="bankTxReason" value={x.reason} />
      </span>
    </span>
  );
}

/** Bank transactions in a table (the list page, a payment request's page). */
export function BankTxTable({ rows, empty, showRequest = true }: { rows: BankTransaction[]; empty?: string; showRequest?: boolean }) {
  const { t } = useTranslation('commerce');
  const columns: Column<BankTransaction>[] = [
    {
      key: 'received',
      header: t('bankTxs.col.received'),
      className: 'whitespace-nowrap',
      cell: (x) => (
        <Link to={bankTxPath(x.id)} className="font-medium text-primary hover:underline" title={formatDateTime(x.received_at)}>
          {formatRelative(x.received_at)}
        </Link>
      ),
    },
    {
      key: 'source',
      header: t('bankTxs.col.source'),
      cell: (x) => (
        <span className="inline-flex flex-col gap-0.5">
          <Label group="bankFeed" value={x.source} />
          <span className={mutedSmall}>{x.bank_name ?? x.instance_key}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('bankTxs.col.amount'),
      numeric: true,
      cell: (x) => <Amount amount={x.amount} currency={x.currency} signed={x.direction === 'OUT' ? 'CREDIT' : undefined} className="font-semibold" />,
    },
    { key: 'content', header: t('bankTxs.col.content'), className: 'whitespace-normal', cell: (x) => <span className="block max-w-80 min-w-40 break-words font-mono text-xs">{x.content || '—'}</span> },
    { key: 'status', header: t('bankTxs.col.status'), cell: (x) => <TxState t={x} /> },
    ...(showRequest
      ? [
          { key: 'request', header: t('bankTxs.col.request'), cell: (x: BankTransaction) => <RequestRef id={x.payment_id} code={x.payment_code} /> },
          { key: 'customer', header: t('bankTxs.col.customer'), cell: (x: BankTransaction) => <CustomerRef id={x.customer_id} name={x.customer_name} /> },
        ]
      : []),
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(x) => x.id} empty={<EmptyState title={empty ?? t('bankTxs.empty')} />} />;
}

/** The bank feeds and their webhook addresses (on the api host). */
function Feeds({ feeds }: { feeds: PaymentModuleInstance[] }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  if (!feeds.length) return <Callout tone="warn">{t('bankTxs.noFeeds')}</Callout>;
  return (
    <Section title={t('bankTxs.feedsTitle')}>
      <ul className="grid gap-3 sm:grid-cols-2">
        {feeds.map((f) => (
          <li key={`${f.module}/${f.instance_key}`} className="min-w-0 rounded-lg border p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">
                {label('bankFeed', f.module)}
                {f.display_name ? <span className="text-muted-foreground"> · {f.display_name}</span> : null}
              </span>
              <span className={f.enabled ? 'text-success' : 'text-muted-foreground'}>{f.enabled ? t('bankTxs.feedOn') : t('bankTxs.feedOff')}</span>
            </div>
            <p className={mutedSmall}>{f.last_received_at ? t('bankTxs.feedLast', { when: formatRelative(f.last_received_at) }) : t('bankTxs.feedNever')}</p>
            <p className="mt-1 flex min-w-0 items-center gap-1 text-xs">
              <span className="shrink-0 text-muted-foreground">{t('bankTxs.webhook')}:</span>
              <CopyText value={f.path} />
            </p>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Which connection's account transfer QR codes show, or why none and where to fix it (ADR-028 decision 6). */
function QrAccountNote({ qr }: { qr: QrAccount }) {
  const { t } = useTranslation('commerce');
  if (qr.state === 'OK') return <p className={mutedSmall}>{t('bankTxs.qr.OK', { connection: qr.connection })}</p>;
  const connections = qr.state === 'NO_ACCOUNT' ? '' : qr.connections.join(', ');
  return (
    <Callout tone="serious" title={t(`bankTxs.qr.${qr.state}.title`)}>
      {t(`bankTxs.qr.${qr.state}.body`, { connections })}{' '}
      <Link to={modulesUrl('payment')} className="font-medium text-primary underline-offset-4 hover:underline">
        {t('bankTxs.qr.settingsLink')}
      </Link>
      .
    </Callout>
  );
}

/** `/sales?tab=bank-transactions`. */
export function BankTransactionsPage() {
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('bankTxs.title'));
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const source = params.get('source') ?? '';
  const q = params.get('q') ?? '';
  const pager = useKeyset(`${status}|${source}|${q}`);
  const result = useApi(
    (signal) =>
      api.listBankTransactions(
        { status: (status || undefined) as BankTxStatus | 'OPEN' | undefined, source: source || undefined, q: q || undefined, limit: 50, before: pager.before },
        { signal },
      ),
    [status, source, q, pager.before],
  );
  const data = result.status === 'success' ? result.data : undefined;
  return (
    <>
      <PageHeader title={t('bankTxs.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="bankTxs.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <div className="flex flex-col gap-6">
        {data ? <QrAccountNote qr={data.qr} /> : null}
        {data ? (
          <StatGrid className="xl:grid-cols-2">
            <StatCard
              label={t('bankTxs.stat.unmatched')}
              value={formatCount(data.counts.unmatched)}
              tone={data.counts.unmatched ? 'warn' : 'neutral'}
              hint={data.counts.unmatched ? t('bankTxs.stat.hint') : undefined}
              to="/sales?tab=bank-transactions&status=UNMATCHED"
              linkLabel={t('bankTxs.stat.unmatched')}
            />
            <StatCard
              label={t('bankTxs.stat.review')}
              value={formatCount(data.counts.review)}
              tone={data.counts.review ? 'serious' : 'neutral'}
              hint={data.counts.review ? t('bankTxs.stat.hint') : undefined}
              to="/sales?tab=bank-transactions&status=REVIEW"
              linkLabel={t('bankTxs.stat.review')}
            />
          </StatGrid>
        ) : null}
        {data ? <Feeds feeds={data.feeds} /> : null}
        <div>
          <FilterBar actions={<ResetFilters show={Boolean(status || source || q)} onReset={() => setParams(clearParams(params, FILTER_KEYS))} />}>
            <FilterSelect label={t('bankTxs.statusFilter')} value={status} options={FILTERS} group="bankTxFilter" className="sm:w-56" onChange={(v) => setParams(setParam(params, 'status', v))} />
            <FilterSelect label={t('bankTxs.sourceFilter')} value={source} options={SOURCES} group="bankFeed" onChange={(v) => setParams(setParam(params, 'source', v))} />
            <FilterText label={t('bankTxs.search')} value={q} placeholder={t('bankTxs.searchPlaceholder')} onChange={(v) => setParams(setParam(params, 'q', v))} />
          </FilterBar>
          <Section flush>
            {result.status === 'loading' ? (
              <div className="px-5">
                <Loading rows={5} />
              </div>
            ) : null}
            {result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={result.reload} title={t('bankTxs.loadError')} />
              </div>
            ) : null}
            {data ? (
              <>
                <BankTxTable rows={data.items} />
                <Pager cursors={pager.cursors} next={data.next_before} onNewer={pager.newer} onOlder={pager.older} />
              </>
            ) : null}
          </Section>
        </div>
      </div>
    </>
  );
}

/** What an action did, in words. */
function Resolved({ r }: { r: BankTxResolved }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const text = r.status === 'REVIEW' ? t('bankTx.result.REVIEW', { reason: label('bankTxReason', r.reason) }) : t(`bankTx.result.${r.status}`);
  return (
    <Callout tone={r.status === 'SETTLED' ? 'ok' : r.status === 'REVIEW' ? 'warn' : 'neutral'}>
      {text}
      {r.order ? <> {label('orderFunding', r.order)}.</> : null}
    </Callout>
  );
}

function AssignForm({ id, initialCode, close, changed }: DialogBodyProps & { id: string; initialCode?: string }) {
  const { t } = useTranslation('commerce');
  const [code, setCode] = useState(initialCode ?? '');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { code: string; reason: string }, opts) => api.assignBankTransaction(id, b, opts));
  const parsed = transferCode(code);
  const wrong = code.trim() !== '' && !parsed;
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Resolved r={w.state.result} />
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (parsed && reason.trim()) void w.run({ code: parsed, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('bankTx.assign.code')} hint={wrong ? <span className="text-critical">{t('bankTx.assign.codeInvalid')}</span> : t('bankTx.assign.codeHint')}>
        <Input className="font-mono" autoComplete="off" value={code} aria-invalid={wrong} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!parsed || !reason.trim()} submit={t('bankTx.assign.submit')} onClose={close} />
    </form>
  );
}

function CreditForm({ id, close, changed }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('commerce');
  const [customer, setCustomer] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { customer_id?: string; reason: string }, opts) => api.creditBankTransaction(id, b, opts));
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Resolved r={w.state.result} />
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) void w.run({ customer_id: customer.trim() || undefined, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('bankTx.credit.customer')} hint={t('bankTx.credit.customerHint')}>
        <Input className="font-mono" autoComplete="off" value={customer} onChange={(e) => setCustomer(e.target.value)} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('bankTx.credit.submit')} onClose={close} />
    </form>
  );
}

function DismissForm({ id, close, changed }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('commerce');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.dismissBankTransaction(id, b, opts));
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Resolved r={w.state.result} />
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) void w.run({ reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('bankTx.dismiss.submit')} onClose={close} danger />
    </form>
  );
}

/** "Tiền quyết toán từ cổng": a gateway paid the money it took into the account; recorded, credited to no wallet. */
function SettlementForm({ id, close, changed }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const [module, setModule] = useState<string>(GATEWAYS[0]);
  const [reason, setReason] = useState('');
  const w = useWrite((b: { module: string; reason: string }, opts) => api.settlementPayout(id, b, opts));
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Resolved r={w.state.result} />
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) void w.run({ module, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('bankTx.settlement.module')}>
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger className="w-full bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GATEWAYS.map((g) => (
              <SelectItem key={g} value={g}>
                {label('bankFeed', g)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('bankTx.settlement.submit')} onClose={close} />
    </form>
  );
}

/** The open requests of the same amount, each with "Gán cho yêu cầu này". */
function Suggestions({ id, rows, onChanged }: { id: string; rows: PaymentRequest[]; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  const access = useAccess();
  const may = mayHandlePayments(access.grants, access.client);
  const columns: Column<PaymentRequest>[] = [
    { key: 'code', header: t('payments.col.code'), cell: (p) => <RequestRef id={p.id} code={p.code} /> },
    { key: 'customer', header: t('payments.col.customer'), cell: (p) => <CustomerRef id={p.customer_id} name={p.customer_name} /> },
    { key: 'amount', header: t('payments.col.amount'), numeric: true, cell: (p) => <Amount amount={p.amount} currency={p.currency} /> },
    { key: 'created', header: t('payments.col.created'), cell: (p) => <When at={p.created_at} /> },
    ...(may
      ? [
          {
            key: 'use',
            header: '',
            cell: (p: PaymentRequest) => (
              <ActionDialog label={t('bankTx.useSuggestion')} icon={Link2} variant="ghost" title={t('bankTx.assign.title')} description={t('bankTx.assign.description')} onChanged={onChanged}>
                {(d) => <AssignForm id={id} initialCode={p.code ?? ''} {...d} />}
              </ActionDialog>
            ),
          },
        ]
      : []),
  ];
  return (
    <Section title={t('bankTx.suggestions')} flush>
      <DataTable columns={columns} rows={rows} rowKey={(p) => p.id} empty={<EmptyState title={t('bankTx.noSuggestions')} />} />
    </Section>
  );
}

/** `/bank-transactions/:transactionId`. */
export function BankTransactionDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { transactionId = '' } = useParams();
  useDocumentTitle(t('bankTx.title'));
  const access = useAccess();
  const result = useApi((signal) => api.getBankTransaction(transactionId, { signal }), [transactionId]);
  const crumbs = [{ label: t('crumb') }, { label: t('bankTxs.title'), to: '/sales?tab=bank-transactions' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('bankTx.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('bankTx.loadError')} />
      </>
    );
  }
  const { transaction: x, payload, suggestions } = result.data;
  const open = (x.status === 'UNMATCHED' || x.status === 'REVIEW') && x.direction === 'IN';
  const may = open && mayHandlePayments(access.grants, access.client);
  const reference = x.bank_reference ?? x.external_id;
  const actions = (
    <>
      {may ? (
        <>
          <ActionDialog label={t('bankTx.assign.action')} icon={Link2} title={t('bankTx.assign.title')} description={t('bankTx.assign.description')} onChanged={result.reload}>
            {(d) => <AssignForm id={x.id} initialCode={x.matched_code ?? ''} {...d} />}
          </ActionDialog>
          <ActionDialog label={t('bankTx.credit.action')} icon={Wallet} title={t('bankTx.credit.title')} description={t('bankTx.credit.description')} onChanged={result.reload}>
            {(d) => <CreditForm id={x.id} {...d} />}
          </ActionDialog>
          <ActionDialog label={t('bankTx.dismiss.action')} icon={Ban} title={t('bankTx.dismiss.title')} description={t('bankTx.dismiss.description')} onChanged={result.reload}>
            {(d) => <DismissForm id={x.id} {...d} />}
          </ActionDialog>
          {x.status === 'UNMATCHED' ? (
            <ActionDialog label={t('bankTx.settlement.action')} icon={Store} title={t('bankTx.settlement.title')} description={t('bankTx.settlement.description')} onChanged={result.reload}>
              {(d) => <SettlementForm id={x.id} {...d} />}
            </ActionDialog>
          ) : null}
        </>
      ) : null}
      <RefreshButton onClick={result.reload} />
    </>
  );
  return (
    <>
      <PageHeader title={t('bankTx.titleFor', { reference })} crumbs={[...crumbs, { label: reference }]} actions={actions} />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('bankTxs.col.status')} value={label('bankTxStatus', x.status)} tone={BANK_TX_STATUS[x.status] ?? 'neutral'} hint={label('bankTxReason', x.reason)} icon={Landmark} />
          <StatCard label={t('bankTxs.col.amount')} value={<Amount amount={x.amount} currency={x.currency} />} hint={label('txDirection', x.direction)} />
          <StatCard label={t('bankTx.field.request')} value={<RequestRef id={x.payment_id} code={x.payment_code} />} hint={x.payment_status ? label('paymentStatus', x.payment_status) : undefined} />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('bankTx.field.source'), `${label('bankFeed', x.source)} · ${x.instance_key}`],
              [t('bankTx.field.externalId'), <CopyText key="e" value={x.external_id} />],
              [t('bankTx.field.direction'), label('txDirection', x.direction)],
              [t('bankTx.field.account'), [x.account_number, x.sub_account].filter(Boolean).join(' / ') || null],
              [t('bankTx.field.content'), <span key="c" className="font-mono text-xs">{x.content || '—'}</span>],
              [t('bankTx.field.code'), x.matched_code ?? x.code_hint],
              [t('bankTx.field.reference'), x.bank_reference],
              [t('bankTx.field.counterparty'), [x.counter_account_name, x.counter_account_number, x.counter_bank].filter(Boolean).join(' · ') || null],
              [t('bankTx.field.booked'), x.transaction_at ? formatDateTime(x.transaction_at) : null],
              [t('bankTx.field.received'), formatDateTime(x.received_at)],
              [t('bankTxs.col.customer'), x.customer_id ? <CustomerRef key="cu" id={x.customer_id} name={x.customer_name} /> : null],
              [t('bankTx.field.resolvedBy'), x.resolved_by_name],
              [t('bankTx.field.resolvedAt'), x.resolved_at ? formatDateTime(x.resolved_at) : null],
              [t('bankTx.field.note'), x.resolution_note],
              [t('bankTx.field.id'), <CopyText key="id" value={x.id} />],
            ]}
          />
        </Section>
        {open ? <Suggestions id={x.id} rows={suggestions} onChanged={result.reload} /> : null}
        <Section title={t('bankTx.payload')}>
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(payload, null, 2)}</pre>
        </Section>
      </div>
    </>
  );
}
